import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import SettingsSection from "./SettingsSection";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "paragraph", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select (dropdown)" },
];

export default function CustomFieldsManager() {
  const [fields, setFields] = useState([]);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = () =>
    api.get("/custom-fields").then((r) => setFields(r.data)).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!label.trim()) { toast.error("Label is required"); return; }
    if (type === "select") {
      const opts = options.split(/[\n,]/).map((o) => o.trim()).filter(Boolean);
      if (opts.length === 0) { toast.error("Select type requires at least one option"); return; }
    }
    setSubmitting(true);
    try {
      const body = { label: label.trim(), type, required };
      if (type === "select") {
        body.options = options.split(/[\n,]/).map((o) => o.trim()).filter(Boolean);
      }
      await api.post("/custom-fields", body);
      setLabel("");
      setType("text");
      setOptions("");
      setRequired(false);
      refresh();
      toast.success("Custom field added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add custom field");
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (f) => {
    try {
      await api.patch(`/custom-fields/${f.id}`, { active: !f.active });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (f) => {
    try {
      await api.delete(`/custom-fields/${f.id}`);
      refresh();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <SettingsSection title="Custom Fields" description="Add custom fields that appear on the ticket form.">
      <div className="surface-card p-5 space-y-3">
        <div className="text-sm font-semibold text-slate-700">Add custom field</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Label</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Customer Reference"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {FIELD_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </div>
        </div>
        {type === "select" && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              Options <span className="text-slate-400">(comma- or line-separated)</span>
            </label>
            <textarea
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              rows={3}
              placeholder="Option A, Option B, Option C"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="cf-required"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            className="rounded border-slate-300"
          />
          <label htmlFor="cf-required" className="text-sm text-slate-700">Required</label>
        </div>
        <Button onClick={add} disabled={submitting}>
          {submitting ? "Adding…" : "Add Field"}
        </Button>
      </div>

      <div className="surface-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Existing fields
        </div>
        <div className="divide-y divide-slate-100">
          {fields.length === 0 && (
            <div className="px-5 py-6 text-xs text-slate-400 text-center">No custom fields yet.</div>
          )}
          {fields.map((f) => (
            <div key={f.id} className="px-5 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className={`text-sm font-medium ${f.active ? "text-slate-800" : "text-slate-400 line-through"}`}>
                  {f.label}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {FIELD_TYPES.find((t) => t.value === f.type)?.label || f.type}
                  {f.required && " · required"}
                  {f.options?.length > 0 && ` · ${f.options.length} option${f.options.length !== 1 ? "s" : ""}`}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  className="text-xs text-slate-500 hover:text-slate-900"
                  onClick={() => toggle(f)}
                >
                  {f.active ? "Disable" : "Enable"}
                </button>
                <button
                  className="text-xs text-rose-500 hover:text-rose-700"
                  onClick={() => del(f)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SettingsSection>
  );
}
